import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Check } from 'lucide-react';

import { ThongBaoHetHanMuc } from '../../../components/shared';
import { Button, Spinner } from '../../../components/ui';
import { useT } from '../../../i18n';
import { cn } from '../../../lib/cn';
import { tachLoiHanMuc } from '../../../lib/loi-han-muc';
import { CAC_BUOC, dangXuLy, viTriBuoc } from '../nghiep-vu/bien-ban';
import type { Phien } from '../types';

/** Dải tiến trình của phiên chưa xong, hoặc câu lỗi kèm Thử lại. Phiên đã xong không vẽ gì. */
export function TienTrinhXuLy({
  phien,
  coSua,
  dangThuLai,
  onThuLai,
}: {
  phien: Phien;
  coSua: boolean;
  dangThuLai: boolean;
  onThuLai: () => void;
}) {
  const { t } = useT();

  if (phien.status === 'FAILED') {
    const hanMuc = tachLoiHanMuc(phien.error_message, phien.error_code);
    if (hanMuc) {
      return (
        <ThongBaoHetHanMuc
          thongTin={hanMuc}
          nhanThuLai={coSua ? t('bienBanXuLy.thuLai') : undefined}
          nutThuLai={<Button variant="primary" size="md" loading={dangThuLai} onClick={onThuLai}>{t('bienBanXuLy.thuLai')}</Button>}
        />
      );
    }
    return (
      <div role="alert" className="flex flex-wrap items-start justify-between gap-3 rounded-lg border border-danger-200 bg-danger-50 px-5 py-4">
        <div className="flex min-w-0 flex-1 basis-72 items-start gap-2.5">
          <ExclamationCircleOutlined aria-hidden className="mt-0.5 text-danger-600" />
          <div className="min-w-0">
            <p className="text-sm font-medium text-danger-700">{t('bienBanXuLy.tieuDeLoi')}</p>
            <p className="mt-0.5 text-sm text-danger-700">{phien.error_message || t('bienBanXuLy.loiMacDinh')}</p>
            {phien.error_code && <p className="mt-1 font-mono text-2xs text-danger-700">{t('bienBanXuLy.maLoi', { ma: phien.error_code })}</p>}
          </div>
        </div>
        {coSua && (
          <Button variant="primary" size="md" loading={dangThuLai} onClick={onThuLai}>
            {t('bienBanXuLy.thuLai')}
          </Button>
        )}
      </div>
    );
  }
  if (!dangXuLy(phien.status)) return null;

  const hienTai = viTriBuoc(phien.status) ?? 0;
  return (
    <div className="rounded-lg border border-default bg-surface px-5 py-4 shadow-sm">
      <ol aria-label={t('bienBanXuLy.nhan')} className="flex flex-wrap items-center gap-x-1.5 gap-y-2">
        {CAC_BUOC.map((b, i) => {
          const xong = i < hienTai;
          const dang = i === hienTai;
          return (
            <li key={b} aria-current={dang ? 'step' : undefined} className="flex min-w-0 items-center gap-1.5">
              {i > 0 && <span aria-hidden className={cn('h-px w-3 xl:w-10', xong || dang ? 'bg-primary-500' : 'bg-[var(--border-default)]')} />}
              <span
                aria-hidden
                className={cn(
                  'flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-medium',
                  xong && 'bg-primary-500 text-white',
                  dang && 'bg-primary-50 text-primary-700',
                  !xong && !dang && 'bg-subtle text-content-muted',
                )}
              >
                {xong ? <Check size={14} /> : dang ? <Spinner size="small" /> : i + 1}
              </span>
              <span className={cn('text-sm', dang ? 'font-medium text-content-primary' : 'text-content-muted')}>
                {t(`bienBanXuLy.buoc.${b}`)}
              </span>
            </li>
          );
        })}
      </ol>
      <p role="status" className="mt-3 text-xs text-content-secondary">
        {t(`bienBanXuLy.moTa.${phien.status as 'QUEUED' | 'TRANSCRIBING' | 'SUMMARIZING'}`)}
      </p>
    </div>
  );
}
